import { ArrowRightIcon, Badge, Button, CalendarIcon, ChevronDownIcon, cn, Paragraph } from '@quickmd-llc/design-system'
import { PatientRow } from './PatientRow'
import { useNavigate } from 'react-router-dom'
import React, { useCallback, useMemo, useState } from 'react'
import { type WaitingRoomV2VisitListItemDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { CALENDAR_PAGE_PATH } from 'utils/constants'
import { useMinuteTick } from 'pages/WaitingRoom/hooks/useMinuteTick'
import { type DismissedVisit, type JustChangeTag } from '../hooks/useVisitLists'

type ScheduleEntry = {
  visit: WaitingRoomV2VisitListItemDoctorApiOutContract
  dismissed?: DismissedVisit
  isNext?: boolean
  reactKey: string
}

const MAX_COLLAPSED_ROWS = 8

export const ScheduleSection: React.FC<{
  list: WaitingRoomV2VisitListItemDoctorApiOutContract[]
  nextVisit?: WaitingRoomV2VisitListItemDoctorApiOutContract | undefined
  justChangeTags?: Record<string, JustChangeTag> | undefined
  dismissedVisits?: DismissedVisit[] | undefined
  onPrepVisitClick: (visit: WaitingRoomV2VisitListItemDoctorApiOutContract) => void
  onDismissVisit?: ((key: string) => void) | undefined
  hideHeader?: boolean | undefined
}> = ({ list, nextVisit, justChangeTags, dismissedVisits, onPrepVisitClick, onDismissVisit, hideHeader = false }) => {
  const navigate = useNavigate()
  const [isExpanded, setIsExpanded] = useState(true)
  const [showAll, setShowAll] = useState(false)
  useMinuteTick()

  const handleOpenCalendar = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      navigate(CALENDAR_PAGE_PATH)
    },
    [navigate],
  )

  const entries = useMemo<ScheduleEntry[]>(() => {
    const activeEntries: ScheduleEntry[] = list.map((visit, index) => ({
      visit,
      reactKey: visit.VisitId ?? `no-id-${visit.DateTime ?? index}`,
    }))
    const dismissedEntries: ScheduleEntry[] = (dismissedVisits ?? [])
      .filter((d) => d.visit.Status !== 'pending')
      .map((d) => ({ visit: d.visit, dismissed: d, reactKey: d.key }))
    const nextEntry: ScheduleEntry[] =
      nextVisit ? [{ visit: nextVisit, isNext: true, reactKey: nextVisit.VisitId ?? 'next' }] : []
    return [...nextEntry, ...activeEntries, ...dismissedEntries].sort(
      (a, b) => new Date(a.visit.DateTime).getTime() - new Date(b.visit.DateTime).getTime(),
    )
  }, [list, dismissedVisits, nextVisit])

  const remainingCount = list.length + (nextVisit ? 1 : 0)
  const pillText = remainingCount > 0 ? `${remainingCount} remaining` : 'None remaining'
  const availableCount = useMemo(
    () => list.filter((v) => v.IsPatientAvailable).length + (nextVisit?.IsPatientAvailable ? 1 : 0),
    [list, nextVisit],
  )
  const isListEmpty = entries.length === 0
  const visibleEntries = showAll ? entries : entries.slice(0, MAX_COLLAPSED_ROWS)
  const hasOverflow = entries.length > MAX_COLLAPSED_ROWS
  const expanded = hideHeader ? true : isExpanded

  return (
    <section className='tailwind-scoped'>
      {!hideHeader && (
        <div
          role='button'
          tabIndex={0}
          aria-expanded={isExpanded}
          className='flex min-h-sp-6 cursor-pointer items-center justify-between py-sp-1 pr-sp-1'
          onClick={() => setIsExpanded((prev) => !prev)}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsExpanded((prev) => !prev)
            }
          }}>
          <div className='flex items-center gap-sp-1'>
            <ChevronDownIcon
              className={cn('text-text-light transition-transform', !isExpanded && '-rotate-90')}
              iconSize='xs'
            />
            <Paragraph className='font-semibold text-text-dark'>Your schedule</Paragraph>
            <Badge size='s' variant='active' className='bg-white'>
              {pillText}
            </Badge>
            {availableCount > 0 && (
              <Paragraph size='sm' className='font-semibold text-text-success'>
                {availableCount} {availableCount === 1 ? 'Patient' : 'Patients'} available
              </Paragraph>
            )}
          </div>
          <Button size='s' priority='tertiary' onClick={handleOpenCalendar}>
            <CalendarIcon /> Full schedule <ArrowRightIcon />
          </Button>
        </div>
      )}
      {expanded ?
        <div className='overflow-hidden rounded-[8px] border border-border-medium bg-white'>
          {isListEmpty ?
            <div className='flex h-sp-6 w-full items-center justify-center px-sp-3 py-sp-1'>
              <Paragraph size='sm' className='text-text-light'>
                No patients scheduled today.
              </Paragraph>
            </div>
          : <>
              {visibleEntries.map((entry) => {
                const tag = entry.visit.VisitId ? justChangeTags?.[entry.visit.VisitId] : undefined
                const justChangeKind = entry.dismissed ? undefined : tag?.kind
                const hasSeenJustChangeFade = !!tag?.seenAt && !tag.fadeCompleted
                const reactKey = entry.dismissed ? entry.reactKey : `${entry.reactKey}-${tag?.seenAt ?? 0}`
                return (
                  <PatientRow
                    key={reactKey}
                    visit={entry.visit}
                    onClick={onPrepVisitClick}
                    isNext={entry.isNext}
                    justChangeKind={justChangeKind}
                    hasSeenJustChangeFade={hasSeenJustChangeFade}
                    dismissed={
                      entry.dismissed ?
                        {
                          kind: entry.dismissed.kind,
                          newDateTime: entry.dismissed.newDateTime,
                          dismissedAt: entry.dismissed.dismissedAt,
                        }
                      : undefined
                    }
                    onDismiss={
                      entry.dismissed ?
                        () => {
                          onDismissVisit?.(entry.dismissed?.key ?? '')
                        }
                      : undefined
                    }
                  />
                )
              })}
              {hasOverflow ?
                <button
                  type='button'
                  onClick={() => setShowAll((prev) => !prev)}
                  className='flex w-full cursor-pointer items-center justify-center gap-sp-1 border-0 bg-white px-sp-3 py-sp-2 transition-colors hover:bg-surface-highlight'>
                  <Paragraph size='sm' className='font-semibold text-text-highlight'>
                    {showAll ? 'See less' : 'See all'}
                  </Paragraph>
                  <ChevronDownIcon
                    className={cn('text-text-highlight transition-transform', showAll && 'rotate-180')}
                    iconSize='xs'
                  />
                </button>
              : null}
            </>
          }
        </div>
      : null}
    </section>
  )
}
